/**
 * The top bar's deadline pill (04 §0, 05 §3): the open case with the nearest deadline; under 6 h a slow 2 s
 * breathing glow, overdue solid; never flashes. Click → the case's stage.
 *
 * Composed from the kit's countdown helpers rather than `CountdownPill`, because its due time must be in the
 * viewer's zone (`GET /me` → `user.timezone`, else the browser's) next to UTC (04 §11); the kit formats in the
 * browser's zone only (REQUESTS #50). The look is the kit's pill.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Timer } from "lucide-react";
import { cn, remaining, urgency, useKitCopy, useNow, type Remaining, type Urgency } from "@brevalt/ui";
import { parseInstant, screenTime } from "@brevalt/app-core";
import type { NextDeadline } from "./open-cases";

const pillTone: Record<Urgency, string> = {
  normal: "border-border bg-surface text-text",
  soon: "border-exploited/40 bg-exploited-bg text-exploited breathe",
  overdue: "border-exploited bg-exploited text-surface",
};

/** Screen-reader cadence for countdowns (04 §15): at most every 10 minutes, urgency changes at once. */
const ANNOUNCE_EVERY_MS = 10 * 60_000;

/** The live region's text; `r` from `remaining(due, now, locale)` with the kit's locale (`r.spoken`, #141 b). */
export function useAnnouncement(r: Remaining, u: Urgency, label: string, now: Date): string {
  const { t } = useTranslation();
  const { locale } = useKitCopy();
  const say = () => {
    const time = r.spoken;
    const base = r.overdue
      ? t("shell.label_overdue_by_time", { label, time })
      : t("shell.label_due_in_time", { label, time });
    if (u === "soon") return `${base}. ${t("ui.less_than_6_hours_left")}.`;
    if (u === "overdue") return `${base}. ${t("ui.the_deadline_has_passed")}.`;
    return base;
  };
  // A new text at most every 10 minutes (wall-clock buckets), and at once when the urgency or the language changes.
  const key = `${u}|${label}|${locale}|${Math.floor(now.getTime() / ANNOUNCE_EVERY_MS)}`;
  const [said, setSaid] = useState(() => ({ key, text: say() }));
  if (said.key !== key) setSaid({ key, text: say() });
  return said.key === key ? said.text : say();
}

export function DeadlinePill({
  deadline,
  label,
  zone,
  compact,
  now: frozen,
  testId = "deadline-pill",
  announce = true,
}: {
  deadline: NextDeadline;
  label: string;
  /** The viewer's zone. */
  zone: string;
  compact?: boolean;
  now?: Date;
  /** The top bar's pill by default; the dashboard's case clocks use their own id. */
  testId?: string;
  /**
   * The live region (04 §15): only the top bar's pill announces; copies elsewhere (the dashboard) keep their
   * `aria-label`, so a screen reader doesn't hear the same deadline twice.
   */
  announce?: boolean;
}) {
  const { t } = useTranslation();
  const now = useNow(frozen);
  const { locale } = useKitCopy();
  const due = parseInstant(deadline.dueAt);
  const r = remaining(due, now, locale);
  const u = urgency(r);
  const said = useAnnouncement(r, u, label, now);
  const time = screenTime(due, zone);
  return (
    <a
      href={`/cases/${deadline.caseId}/stages/${deadline.stage}`}
      data-urgency={u}
      data-testid={testId}
      aria-label={t("shell.deadline_pill_label", {
        label,
        local: time.showUtc ? `${time.local} ${time.offset}` : time.local,
        utc: time.utc,
        caseId: deadline.humanId,
      })}
      className={cn(
        "inline-flex h-touch items-center gap-2 rounded-full border px-3 text-xs font-medium lg:h-9",
        pillTone[u],
      )}
    >
      <Timer aria-hidden className="size-4" strokeWidth={1.75} />
      <span aria-hidden className="flex items-center gap-1.5">
        {!compact && (
          <span>{r.overdue ? t("shell.label_overdue_by", { label }) : t("shell.label_due_in", { label })}</span>
        )}
        <span className="tnum font-semibold">{r.text}</span>
        {!compact && <span className="max-xl:hidden">{t("shell.pill_case_id", { caseId: deadline.humanId })}</span>}
      </span>
      {announce && (
        <span className="sr-only" aria-live="polite">
          {said}
        </span>
      )}
    </a>
  );
}
